import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { TrustSection, ValuesSection } from "@/components/TrustBlocks";
import RentalSummary from "@/components/RentalSummary";
import CameraCapture from "@/components/CameraCapture";
import SignaturePad from "@/components/SignaturePad";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ArrowRight, Camera, FileCheck, Check, Users, Package, Globe, Flag, Mail } from "lucide-react";
import { toast, Toaster } from "sonner";

const STEPS = ["vehicle", "config", "customer", "id", "signature", "review"];

const pad = (n) => String(n).padStart(2, "0");
const todayDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fmtDt = (date, hh, mm) =>
  date ? `${date}T${pad(hh)}:${pad(mm)}` : "";
const addDays = (dateStr, days) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// ----- Vito pricing (mirror of backend) -----
const VITO_TARIFF = {
  ch_weekday: { 1: 190, 2: 380, 3: 540, 4: 720, 5: 900, 6: 1020, 7: 1190 },
  ch_weekend: { 1: 200, 2: 380 },
  eu_weekday: { 1: 230, 2: 460, 3: 660, 4: 880, 5: 1050, 6: 1260, 7: 1400 },
  eu_weekend: { 1: 250, 2: 480 },
  extra_day_chf: { ch: 170, eu: 200 },
};
const isPureWeekend = (startDateStr, days) => {
  if (!startDateStr || days < 1 || days > 2) return false;
  for (let i = 0; i < days; i++) {
    const d = new Date(startDateStr);
    d.setDate(d.getDate() + i);
    const wd = d.getDay(); // 0=Sun..6=Sat
    if (wd !== 0 && wd !== 6) return false;
  }
  return true;
};
const computeVitoPrice = (destination, startDateStr, days) => {
  if (!startDateStr || !days) return 0;
  const dest = (destination || "ch").toLowerCase();
  const weekdayTable = VITO_TARIFF[`${dest}_weekday`];
  const weekendTable = VITO_TARIFF[`${dest}_weekend`];
  if (isPureWeekend(startDateStr, days) && weekendTable[days]) return weekendTable[days];
  if (days <= 7) return weekdayTable[days] || 0;
  return weekdayTable[7] + (days - 7) * VITO_TARIFF.extra_day_chf[dest];
};

// ----- Master packages -----
const MASTER_PACKAGES = [
  { hours: 3, price_chf: 80, km_included: 150 },
  { hours: 6, price_chf: 150, km_included: 250 },
  { hours: 10, price_chf: 190, km_included: 350 },
];
const MASTER_OPEN = 8;
const MASTER_CLOSE = 20;
const isMasterDateOk = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d.getDay() !== 3; // Wednesday = 3
};

const StepIndicator = ({ step, labels }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid="step-indicator">
    {labels.map((l, i) => {
      const active = i === step;
      const done = i < step;
      return (
        <div key={l} className="flex items-center gap-2">
          <div
            className={`flex items-center gap-2 px-3 py-2 border ${
              active
                ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white"
                : done
                ? "border-[var(--rs-primary)] text-[var(--rs-primary)]"
                : "border-[var(--rs-border)] text-[var(--rs-text-muted)]"
            }`}
          >
            <span className="text-[10px] font-bold w-5 h-5 flex items-center justify-center border border-current">
              {done ? <Check size={12} /> : i + 1}
            </span>
            <span className="text-[11px] uppercase font-bold tracking-[0.15em]">{l}</span>
          </div>
          {i < labels.length - 1 && <span className="text-[var(--rs-border)]">—</span>}
        </div>
      );
    })}
  </div>
);

// 2-stage vehicle picker:
//   Stage A: Choose vehicle TYPE (Personentransporter / Warentransporter)
//   Stage B: Choose STATION — system auto-picks first matching vehicle
const VehicleStep = ({ vehicles, stations, selected, onSelect }) => {
  const { t } = useLanguage();
  // Group vehicles by tariff_type (vito_daily = Personen, master_package = Waren)
  const vitoList = vehicles.filter((v) => v.tariff_type === "vito_daily");
  const masterList = vehicles.filter((v) => v.tariff_type === "master_package");

  // Internal stage state, derived from current selection
  const [pickedType, setPickedType] = useState(
    selected?.tariff_type === "master_package" ? "master" : selected ? "vito" : null
  );

  // Build station options for the picked type — deduped by station id
  const stationOptions = useMemo(() => {
    if (!pickedType) return [];
    const list = pickedType === "vito" ? vitoList : masterList;
    const seen = new Map();
    list.forEach((v) => {
      // Merge BOTH primary station_id AND flexible_stations so a vehicle
      // can be picked up at any allowed location.
      const sids = Array.from(new Set([
        ...(v.station_id ? [v.station_id] : []),
        ...(v.flexible_stations || []),
      ]));
      sids.forEach((sid) => {
        if (!seen.has(sid)) {
          const st = stations.find((x) => x.id === sid);
          if (st) {
            // store a representative vehicle for this station+type
            const matchingVehicles = list.filter(
              (x) =>
                x.station_id === sid ||
                (x.flexible_stations || []).includes(sid)
            );
            seen.set(sid, { station: st, vehicles: matchingVehicles });
          }
        }
      });
    });
    return Array.from(seen.values());
  }, [pickedType, vitoList, masterList, stations]);

  const handleTypePick = (type) => {
    setPickedType(type);
    // If only one station has this type, auto-select its first vehicle
    // Otherwise wait for station pick
    onSelect(null); // reset selection so user has to confirm station
  };

  const handleStationPick = (sid, vehiclesForStation) => {
    // Auto-pick the first vehicle at this station (option a — customer-friendly)
    // Prefer a vehicle whose primary station_id matches; otherwise take first
    const primary =
      vehiclesForStation.find((v) => v.station_id === sid) ||
      vehiclesForStation[0];
    if (primary) {
      // Set the user's station preference via the vehicle's pre-selected station
      // The parent's useEffect will sync stationId from vehicle.station_id
      // But if user picked an alternate station via flexible_stations, we need to
      // signal that — we patch the vehicle with a hint property the parent reads.
      onSelect({ ...primary, _preferred_station_id: sid });
    }
  };

  return (
    <div className="rs-fade-up">
      <div className="rs-overline">{t("step_vehicle")}</div>
      <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tight mt-2 leading-[0.95]">
        {pickedType ? t("vehicle_pick_station_title") : t("vehicle_pick_type_title")}
      </h1>
      <p className="text-[var(--rs-text-muted)] mt-3 max-w-2xl">
        {pickedType ? t("vehicle_pick_station_sub") : t("vehicle_pick_type_sub")}
      </p>

      {!pickedType && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {[
            {
              id: "vito",
              icon: Users,
              title: t("vehicle_type_passenger"),
              subtitle: t("vehicle_type_passenger_sub"),
              pill: t("price_from_per_day"),
              image: vitoList[0]?.image_url,
              available: vitoList.length > 0,
            },
            {
              id: "master",
              icon: Package,
              title: t("vehicle_type_cargo"),
              subtitle: t("vehicle_type_cargo_sub"),
              pill: t("price_from_per_3h"),
              image: masterList[0]?.image_url,
              available: masterList.length > 0,
            },
          ].map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={!opt.available}
                onClick={() => handleTypePick(opt.id)}
                data-testid={`vehicle-type-${opt.id}`}
                className={`text-left bg-white border-2 border-[var(--rs-border)] hover:border-[var(--rs-primary)] transition-colors group disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <div className="aspect-[16/10] bg-[var(--rs-surface)] overflow-hidden relative">
                  {opt.image && (
                    <img
                      src={opt.image}
                      alt={opt.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.2em] bg-[var(--rs-primary)] text-white px-2 py-1 flex items-center gap-1">
                    <Icon size={11} /> {opt.title}
                  </span>
                  <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-[0.2em] bg-white text-[var(--rs-primary)] px-2 py-1">
                    {opt.pill}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-[Chivo] text-3xl font-black">{opt.title}</h3>
                  <div className="text-sm text-[var(--rs-text-muted)] mt-2">{opt.subtitle}</div>
                  <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[var(--rs-cta)]">
                    {t("btn_select")} <ArrowRight size={14} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {pickedType && (
        <div className="mt-8">
          <button
            type="button"
            onClick={() => {
              setPickedType(null);
              onSelect(null);
            }}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--rs-text-muted)] hover:text-[var(--rs-primary)] mb-6"
            data-testid="vehicle-type-back"
          >
            <ArrowLeft size={14} /> {t("change_vehicle_type")}
          </button>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stationOptions.map(({ station, vehicles: vs }) => {
              // Selection = exactly what the user picked (or the vehicle's primary
              // station if nothing has been picked yet). Never highlight via
              // `flexible_stations` — that would mark multiple cards as selected.
              const activeSid = selected?._preferred_station_id || selected?.station_id;
              const isSel = !!selected && activeSid === station.id;
              return (
                <button
                  key={station.id}
                  type="button"
                  onClick={() => handleStationPick(station.id, vs)}
                  data-testid={`vehicle-station-${station.id}`}
                  className={`text-left p-5 border-2 transition-colors ${
                    isSel
                      ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white"
                      : "border-[var(--rs-border)] hover:border-[var(--rs-primary)]"
                  }`}
                >
                  <div
                    className={`text-[10px] uppercase tracking-[0.25em] ${
                      isSel ? "text-white/70" : "text-[var(--rs-text-muted)]"
                    }`}
                  >
                    {t("station_label")}
                  </div>
                  <div className="font-[Chivo] text-2xl font-black mt-2 leading-tight">
                    {station.name}
                  </div>
                  <div
                    className={`text-xs mt-2 ${
                      isSel ? "text-white/80" : "text-[var(--rs-text-muted)]"
                    }`}
                  >
                    {station.full}
                  </div>
                  {isSel && (
                    <div className="mt-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em]">
                      <Check size={12} /> {t("selected_label")}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {stationOptions.length === 0 && (
            <div className="text-sm text-[var(--rs-text-muted)] italic mt-6">
              {t("no_stations_available")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// (deprecated helper kept for backwards compat; no longer used)
// eslint-disable-next-line no-unused-vars
const _t_or_default = (key, fallback) => fallback;

const StationPicker = ({ vehicle, stations, value, onChange, isStaff }) => {
  const { t } = useLanguage();
  if (!vehicle) return null;
  const defaultStationId = vehicle.station_id;
  const defaultStation = defaultStationId ? stations.find((s) => s.id === defaultStationId) : null;
  const flexList = vehicle.flexible_stations || [];

  // Fully fixed (no flex options) → show as info only
  if (defaultStation && flexList.length === 0) {
    return (
      <div className="border-2 border-[var(--rs-border)] bg-[var(--rs-surface)] p-4" data-testid="station-picker">
        <div className="rs-overline mb-2">{t("pickup_location")}</div>
        <div className="font-bold">{defaultStation.name}</div>
        <div className="text-sm text-[var(--rs-text-muted)] mt-1">{defaultStation.full}</div>
      </div>
    );
  }

  // Has flexible options:
  //  - Customer (non-staff): show default as info (no picker)
  //  - Staff / Admin: show picker so they can switch
  if (!isStaff && defaultStation) {
    return (
      <div className="border-2 border-[var(--rs-border)] bg-[var(--rs-surface)] p-4" data-testid="station-picker">
        <div className="rs-overline mb-2">{t("pickup_location")}</div>
        <div className="font-bold">{defaultStation.name}</div>
        <div className="text-sm text-[var(--rs-text-muted)] mt-1">{defaultStation.full}</div>
      </div>
    );
  }

  // Staff (or purely flexible, no default) → buttons
  const options = Array.from(new Set([
    ...(defaultStationId ? [defaultStationId] : []),
    ...flexList,
  ]));
  return (
    <div data-testid="station-picker">
      <div className="rs-label mb-3">
        {t("pickup_location_pick")} {isStaff && <span className="text-[var(--rs-text-muted)] ml-2 text-[10px]">(Admin/Station)</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((sid) => {
          const s = stations.find((x) => x.id === sid);
          if (!s) return null;
          const sel = value === sid;
          return (
            <button
              key={sid}
              type="button"
              onClick={() => onChange(sid)}
              data-testid={`station-${sid}`}
              className={`text-left p-4 border-2 transition-colors ${
                sel ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white" : "border-[var(--rs-border)] hover:border-[var(--rs-primary)]"
              }`}
            >
              <div className="font-[Chivo] text-lg font-black">{s.name}</div>
              <div className={`text-xs mt-1 ${sel ? "text-white/80" : "text-[var(--rs-text-muted)]"}`}>{s.full}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const VitoConfigStep = ({ destination, setDestination, startDate, setStartDate, startTime, setStartTime, endDate, setEndDate, endTime, setEndTime, days, vehicle, stations, stationId, setStationId, isStaff }) => {
  const { t } = useLanguage();
  const price = useMemo(() => computeVitoPrice(destination, startDate, days), [destination, startDate, days]);
  const weekend = isPureWeekend(startDate, days);
  return (
    <div className="rs-fade-up">
      <div className="rs-overline">{t("vito_config_eyebrow")}</div>
      <h1 className="font-[Chivo] text-4xl font-black tracking-tight mt-2">{t("vito_config_title")}</h1>
      <p className="text-[var(--rs-text-muted)] mt-2">{t("vito_config_sub")}</p>

      <div className="mt-8 space-y-6">
        <StationPicker vehicle={vehicle} stations={stations} value={stationId} onChange={setStationId} isStaff={isStaff} />
        <div>
          <div className="rs-label mb-3">{t("destination_label")}</div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: "ch", label: t("destination_ch"), icon: Flag, sub: t("destination_ch_sub") },
              { id: "eu", label: t("destination_eu"), icon: Globe, sub: t("destination_eu_sub") },
            ].map((opt) => {
              const Icon = opt.icon;
              const sel = destination === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDestination(opt.id)}
                  data-testid={`vito-destination-${opt.id}`}
                  className={`text-left p-5 border-2 transition-colors ${
                    sel ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white" : "border-[var(--rs-border)] hover:border-[var(--rs-primary)]"
                  }`}
                >
                  <Icon size={24} />
                  <div className="font-[Chivo] text-xl font-black mt-3">{opt.label}</div>
                  <div className={`text-xs mt-1 ${sel ? "text-white/80" : "text-[var(--rs-text-muted)]"}`}>{opt.sub}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="block">
            <span className="rs-label">{t("start_date")}</span>
            <input
              type="date"
              className="rs-input"
              value={startDate}
              onChange={(e) => {
                const v = e.target.value;
                setStartDate(v);
                // keep end ≥ start
                if (v && endDate && new Date(endDate) < new Date(v)) {
                  const d = new Date(v); d.setDate(d.getDate() + 1);
                  setEndDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
                }
              }}
              min={todayDate()}
              data-testid="vito-start-date"
            />
          </label>
          <label className="block">
            <span className="rs-label">{t("start_time")}</span>
            <input
              type="time"
              step="900"
              className="rs-input"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              data-testid="vito-start-time"
            />
            <p className="text-xs text-[var(--rs-text-muted)] mt-2">{t("handover_time_hint")}</p>
          </label>
          <label className="block">
            <span className="rs-label">Rückgabe-Datum</span>
            <input
              type="date"
              className="rs-input"
              value={endDate}
              min={startDate || todayDate()}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="vito-end-date"
            />
          </label>
          <label className="block">
            <span className="rs-label">Rückgabe-Zeit</span>
            <input
              type="time"
              step="900"
              className="rs-input"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              data-testid="vito-end-time"
            />
            <p className="text-xs text-[var(--rs-text-muted)] mt-2">Mietdauer: <strong>{days} {days === 1 ? "Tag" : "Tage"}</strong></p>
          </label>
        </div>

        {vehicle && (
          <AvailabilityCalendar
            vehicleId={vehicle.id}
            startDate={startDate}
            endDate={endDate}
            onPickStart={(d) => { setStartDate(d); if (endDate && new Date(endDate) < new Date(d)) setEndDate(d); }}
            onPickEnd={(d) => setEndDate(d)}
          />
        )}

        {/* Preisanzeige */}
        <div className="border-2 border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white p-5" data-testid="vito-price-block">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">{t("total_price")}</div>
              <div className="font-[Chivo] text-4xl font-black mt-1">CHF {price}.–</div>
            </div>
            <div className="text-xs text-white/70 leading-relaxed text-right">
              {destination === "eu" ? t("destination_eu") : t("destination_ch")} · {days || 1} {(days || 1) === 1 ? t("day_singular") : t("days")}<br />
              {weekend ? t("weekend_tariff") : t("weekday_tariff")}
            </div>
          </div>
          <div className="text-[11px] text-white/60 mt-3 uppercase tracking-[0.15em]">
            {t("vito_price_footer")}
          </div>
        </div>
      </div>
    </div>
  );
};

const MasterConfigStep = ({ packageHours, setPackageHours, startDate, setStartDate, startTime, setStartTime, expectedKm, setExpectedKm, vehicle, stations, stationId, setStationId, isStaff }) => {
  const { t } = useLanguage();
  const dateOk = isMasterDateOk(startDate);
  const timeNum = parseInt((startTime || "08:00").split(":")[0], 10);
  const timeOk = timeNum >= MASTER_OPEN && timeNum < MASTER_CLOSE;
  const pkg = MASTER_PACKAGES.find((p) => p.hours === packageHours);
  return (
    <div className="rs-fade-up">
      <div className="rs-overline">{t("master_config_eyebrow")}</div>
      <h1 className="font-[Chivo] text-4xl font-black tracking-tight mt-2">{t("master_config_title")}</h1>
      <p className="text-[var(--rs-text-muted)] mt-2">{t("master_config_sub")}</p>

      <div className="mt-8 space-y-6">
        <StationPicker vehicle={vehicle} stations={stations} value={stationId} onChange={setStationId} isStaff={isStaff} />
        <div>
          <div className="rs-label mb-3">{t("package_label")}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {MASTER_PACKAGES.map((p) => {
              const sel = packageHours === p.hours;
              return (
                <button
                  key={p.hours}
                  type="button"
                  onClick={() => setPackageHours(p.hours)}
                  data-testid={`master-package-${p.hours}`}
                  className={`text-left p-5 border-2 transition-colors ${
                    sel ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white" : "border-[var(--rs-border)] hover:border-[var(--rs-primary)]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-[Chivo] text-2xl font-black">{p.hours}h</div>
                    <div className={`text-[10px] uppercase tracking-[0.2em] ${sel ? "text-white/80" : "text-[var(--rs-text-muted)]"}`}>
                      {p.km_included} {t("km_included")}
                    </div>
                  </div>
                  <div className="mt-3 text-3xl font-[Chivo] font-black">CHF {p.price_chf}.–</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <label className="block">
            <span className="rs-label">{t("date_label")}</span>
            <input
              type="date"
              className={`rs-input ${!dateOk && startDate ? "border-red-600 text-red-700" : ""}`}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={todayDate()}
              data-testid="master-start-date"
            />
            {startDate && !dateOk && (
              <p className="text-xs text-red-600 font-bold mt-2">⛔ {t("wednesday_blocked")}</p>
            )}
          </label>
          <label className="block">
            <span className="rs-label">{t("start_time_range")}</span>
            <input
              type="time"
              className={`rs-input ${!timeOk ? "border-red-600 text-red-700" : ""}`}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              min="08:00"
              max="20:00"
              step="900"
              data-testid="master-start-time"
            />
            {!timeOk && (
              <p className="text-xs text-red-600 font-bold mt-2">{t("opening_hours_only")}</p>
            )}
          </label>
          <label className="block">
            <span className="rs-label">{t("expected_km_opt")}</span>
            <input
              type="number"
              min="0"
              className="rs-input"
              value={expectedKm}
              onChange={(e) => setExpectedKm(e.target.value)}
              placeholder={pkg ? `${pkg.km_included} ${t("km_included_short")}` : ""}
              data-testid="master-expected-km"
            />
          </label>
        </div>

        {vehicle && (
          <AvailabilityCalendar
            vehicleId={vehicle.id}
            startDate={startDate}
            endDate={startDate}
            onPickStart={(d) => setStartDate(d)}
            onPickEnd={(d) => setStartDate(d)}
          />
        )}

        {/* Preisanzeige */}
        <div className="border-2 border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white p-5" data-testid="master-price-block">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">{t("package_price")}</div>
              <div className="font-[Chivo] text-4xl font-black mt-1">CHF {pkg?.price_chf || 0}.–</div>
            </div>
            <div className="text-xs text-white/70 leading-relaxed text-right">
              {packageHours}h · {pkg?.km_included || 0} {t("km_included")}<br />
              {t("extra_km_overtime_info")}
            </div>
          </div>
          <div className="text-[11px] text-white/60 mt-3 uppercase tracking-[0.15em]">
            {t("master_price_footer")}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required, testid, placeholder, disabled, min }) => (
  <label className="block">
    <span className="rs-label">
      {label} {required && <span className="text-[var(--rs-primary)]">*</span>}
    </span>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      data-testid={testid}
      className={`rs-input ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
    />
  </label>
);

const CustomerStep = ({ customer, setCustomer, isStaff }) => {
  const { t } = useLanguage();
  const upd = (k) => (val) => setCustomer({ ...customer, [k]: val });
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!isStaff || !searchQ || searchQ.trim().length < 2) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/customers/search?q=${encodeURIComponent(searchQ.trim())}`);
        setResults(data || []);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchQ, isStaff]);

  const applyResult = (r) => {
    setCustomer({
      full_name: r.full_name || "",
      address: r.address || "",
      city: r.city || "",
      postal_code: r.postal_code || "",
      phone: r.phone || "",
      email: r.email || "",
      license_number: r.license_number || "",
      date_of_birth: r.date_of_birth || "",
    });
    setSearchQ("");
    setResults([]);
  };

  return (
    <div className="rs-fade-up">
      <div className="rs-overline">{t("step_customer")}</div>
      <h1 className="font-[Chivo] text-4xl font-black tracking-tight mt-2">{t("customer_title")}</h1>
      <p className="text-[var(--rs-text-muted)] mt-2">{t("customer_sub")}</p>

      {isStaff && (
        <div className="mt-6 bg-[var(--rs-surface)] border border-[var(--rs-primary)] p-4" data-testid="customer-search-block">
          <div className="rs-overline mb-2">{t("customer_search_title")}</div>
          <input
            className="rs-input"
            placeholder={t("customer_search_placeholder")}
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            data-testid="customer-search-input"
          />
          {searching && <div className="text-xs text-[var(--rs-text-muted)] mt-2">{t("searching")}</div>}
          {results.length > 0 && (
            <div className="mt-3 max-h-60 overflow-y-auto border border-[var(--rs-border)] bg-white">
              {results.map((r, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={() => applyResult(r)}
                  className="w-full text-left px-4 py-2 border-b last:border-b-0 border-[var(--rs-border)] hover:bg-[var(--rs-primary)] hover:text-white transition-colors"
                  data-testid={`customer-result-${idx}`}
                >
                  <div className="font-bold text-sm">{r.full_name}</div>
                  <div className="text-xs opacity-70">
                    {r.license_number} · {r.phone} {r.email ? `· ${r.email}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
          {!searching && searchQ.length >= 2 && results.length === 0 && (
            <div className="text-xs text-[var(--rs-text-muted)] mt-2">{t("no_results")}</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        <Field label={t("f_full_name")} value={customer.full_name} onChange={upd("full_name")} required testid="cust-name" />
        <Field label="Firma (optional)" value={customer.company || ""} onChange={upd("company")} testid="cust-company" />
        <Field label={t("f_license")} value={customer.license_number} onChange={upd("license_number")} required testid="cust-license" />
        <Field label={t("f_address")} value={customer.address} onChange={upd("address")} required testid="cust-address" />
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("f_postal")} value={customer.postal_code} onChange={upd("postal_code")} required testid="cust-postal" />
          <div className="col-span-2">
            <Field label={t("f_city")} value={customer.city} onChange={upd("city")} required testid="cust-city" />
          </div>
        </div>
        <Field label={t("f_phone")} value={customer.phone} onChange={upd("phone")} type="tel" required testid="cust-phone" />
        <Field label={t("f_email")} value={customer.email} onChange={upd("email")} type="email" testid="cust-email" />
        <Field label={t("f_dob")} value={customer.date_of_birth} onChange={upd("date_of_birth")} type="date" testid="cust-dob" />
      </div>
    </div>
  );
};

const IdStep = ({ idPhoto, setIdPhoto, idPhotoBack, setIdPhotoBack }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [openBack, setOpenBack] = useState(false);
  return (
    <div className="rs-fade-up">
      <div className="rs-overline">{t("step_id")}</div>
      <h1 className="font-[Chivo] text-4xl font-black tracking-tight mt-2">{t("id_title")}</h1>
      <p className="text-[var(--rs-text-muted)] mt-2">{t("id_sub")}</p>

      {/* Front side */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="aspect-[1.586/1] border-2 border-dashed border-[var(--rs-text)] bg-[var(--rs-surface)] flex items-center justify-center overflow-hidden">
          {idPhoto ? (
            <img src={idPhoto} alt="ID" className="w-full h-full object-cover" data-testid="id-photo-thumb" />
          ) : (
            <div className="flex flex-col items-center gap-3 text-[var(--rs-text-muted)]">
              <Camera size={32} />
              <span className="rs-overline">VORDERSEITE · 1.586:1</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4">
          <div className="rs-overline">Vorderseite</div>
          <button type="button" className="rs-btn-primary w-full" onClick={() => setOpen(true)} data-testid="open-camera-btn">
            <Camera size={18} className="mr-2" />
            {idPhoto ? t("btn_retake") : t("btn_capture")}
          </button>
          {idPhoto && (
            <button type="button" className="rs-btn-secondary w-full" onClick={() => setIdPhoto("")} data-testid="remove-id-btn">
              {t("btn_clear")}
            </button>
          )}
        </div>
      </div>

      {/* Back side */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="aspect-[1.586/1] border-2 border-dashed border-[var(--rs-border)] bg-[var(--rs-surface)] flex items-center justify-center overflow-hidden">
          {idPhotoBack ? (
            <img src={idPhotoBack} alt="ID Rückseite" className="w-full h-full object-cover" data-testid="id-back-thumb" />
          ) : (
            <div className="flex flex-col items-center gap-3 text-[var(--rs-text-muted)]">
              <Camera size={32} />
              <span className="rs-overline">RÜCKSEITE (optional)</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4">
          <div className="rs-overline">Rückseite (optional)</div>
          <button type="button" className="rs-btn-secondary w-full" onClick={() => setOpenBack(true)} data-testid="open-camera-back-btn">
            <Camera size={18} className="mr-2" />
            {idPhotoBack ? "Rückseite neu" : "Rückseite aufnehmen"}
          </button>
          {idPhotoBack && (
            <button type="button" className="rs-btn-ghost w-full" onClick={() => setIdPhotoBack("")} data-testid="remove-id-back-btn">
              Rückseite entfernen
            </button>
          )}
        </div>
      </div>

      {open && (
        <CameraCapture
          initialImage={idPhoto}
          onCancel={() => setOpen(false)}
          onCapture={(d) => { setIdPhoto(d); setOpen(false); }}
        />
      )}
      {openBack && (
        <CameraCapture
          initialImage={idPhotoBack}
          onCancel={() => setOpenBack(false)}
          onCapture={(d) => { setIdPhotoBack(d); setOpenBack(false); }}
        />
      )}
    </div>
  );
};

const SignatureStep = ({ signature, setSignature }) => {
  const { t } = useLanguage();
  return (
    <div className="rs-fade-up">
      <div className="rs-overline">{t("step_signature")}</div>
      <h1 className="font-[Chivo] text-4xl font-black tracking-tight mt-2">{t("sig_title")}</h1>
      <p className="text-[var(--rs-text-muted)] mt-2">{t("sig_sub")}</p>
      <div className="mt-8" data-testid="signature-step">
        <SignaturePad value={signature} onChange={setSignature} height={260} />
      </div>
    </div>
  );
};

const ReviewStep = ({ vehicle, customer, rentalType, rentalStart, rentalEnd, priceChf, summary,
                     idPhoto, signature, agb, agbAccepted, setAgbAccepted, defectsNote, setDefectsNote,
                     paymentMethod, setPaymentMethod }) => {
  const { t } = useLanguage();
  return (
    <div className="rs-fade-up" data-testid="review-step">
      <div className="rs-overline">{t("step_review")}</div>
      <h1 className="font-[Chivo] text-4xl font-black tracking-tight mt-2">{t("review_title")}</h1>
      <p className="text-[var(--rs-text-muted)] mt-2">{t("review_sub")}</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-[var(--rs-border)] p-5">
          <div className="rs-overline mb-3">{t("summary_vehicle")}</div>
          <div className="font-bold">{vehicle?.name}</div>
          <div className="text-sm text-[var(--rs-text-muted)]">{vehicle?.plate}</div>
          <div className="rs-overline mt-5 mb-2">{t("th_period")}</div>
          <div className="text-sm">{rentalStart?.replace("T", " · ")}</div>
          <div className="text-sm">→ {rentalEnd?.replace("T", " · ")}</div>
          <div className="text-sm mt-2 text-[var(--rs-text-muted)]">{summary}</div>
          <div className="mt-4 border-t border-[var(--rs-border)] pt-3 flex items-center justify-between">
            <span className="rs-overline">{t("total_price")}</span>
            <span className="font-[Chivo] text-2xl font-black text-[var(--rs-primary)]">CHF {priceChf}.–</span>
          </div>
        </div>
        <div className="bg-white border border-[var(--rs-border)] p-5">
          <div className="rs-overline mb-3">{t("th_customer")}</div>
          <div className="font-bold">{customer.full_name}</div>
          <div className="text-sm text-[var(--rs-text-muted)]">
            {customer.address}, {customer.postal_code} {customer.city}
          </div>
          <div className="text-sm mt-2">
            <span className="rs-overline mr-2">{t("f_license")}</span>
            {customer.license_number}
          </div>
          <div className="text-sm mt-1">
            <span className="rs-overline mr-2">{t("f_phone")}</span>
            {customer.phone}
          </div>
        </div>
        <div className="bg-white border border-[var(--rs-border)] p-5">
          <div className="rs-overline mb-3">{t("step_id")}</div>
          {idPhoto ? (
            <img src={idPhoto} alt="ID" className="w-full aspect-[1.586/1] object-cover border border-[var(--rs-border)]" />
          ) : (
            <div className="text-sm text-[var(--rs-text-muted)]">—</div>
          )}
        </div>
        <div className="bg-white border border-[var(--rs-border)] p-5">
          <div className="rs-overline mb-3">{t("step_signature")}</div>
          {signature ? (
            <img src={signature} alt="Signature" className="w-full h-40 object-contain border border-[var(--rs-border)] bg-white" />
          ) : (
            <div className="text-sm text-[var(--rs-text-muted)]">—</div>
          )}
        </div>
      </div>

      {/* Payment Method */}
      <div className="mt-6 bg-white border-2 border-[var(--rs-primary)] p-5" data-testid="payment-method-block">
        <div className="rs-overline mb-3">{t("payment_method_label")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setPaymentMethod("twint")}
            className={`text-left p-4 border-2 transition-colors ${
              paymentMethod === "twint"
                ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white"
                : "border-[var(--rs-border)] hover:border-[var(--rs-primary)]"
            }`}
            data-testid="payment-twint-btn"
          >
            <div className="font-[Chivo] text-lg font-black">TWINT</div>
            <div className={`text-xs mt-1 ${paymentMethod === "twint" ? "text-white/80" : "text-[var(--rs-text-muted)]"}`}>
              {t("payment_twint_sub")}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setPaymentMethod("cash")}
            className={`text-left p-4 border-2 transition-colors ${
              paymentMethod === "cash"
                ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white"
                : "border-[var(--rs-border)] hover:border-[var(--rs-primary)]"
            }`}
            data-testid="payment-cash-btn"
          >
            <div className="font-[Chivo] text-lg font-black">{t("payment_cash_title")}</div>
            <div className={`text-xs mt-1 ${paymentMethod === "cash" ? "text-white/80" : "text-[var(--rs-text-muted)]"}`}>
              {t("payment_cash_sub")}
            </div>
          </button>
        </div>
      </div>

      <div className="mt-8 bg-white border border-[var(--rs-border)] p-5" data-testid="defects-block">
        <label className="block">
          <span className="rs-label">{t("defects_label")}</span>
          <textarea
            value={defectsNote || ""}
            onChange={(e) => setDefectsNote(e.target.value)}
            placeholder={t("defects_placeholder")}
            rows={3}
            className="rs-input min-h-[80px] resize-y"
            data-testid="defects-textarea"
          />
        </label>
        <p className="text-xs text-[var(--rs-text-muted)] mt-2">{t("defects_helper")}</p>
      </div>

      <div className="mt-6 border-2 border-[var(--rs-primary)] bg-white" data-testid="agb-block">
        <div className="bg-[var(--rs-primary)] text-white px-5 py-3 flex items-baseline justify-between">
          <h3 className="font-[Chivo] font-black text-lg">{t("agb_title")}</h3>
          {agb?.version && (
            <span className="text-[10px] tracking-[0.2em] uppercase text-white/70">v{agb.version}</span>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto p-5 text-sm space-y-4" data-testid="agb-scroll">
          {(agb?.points || []).map((p) => (
            <div key={p.title}>
              <div className="font-bold text-[var(--rs-primary)]">{p.title}</div>
              <div className="text-[var(--rs-text)] mt-1 leading-relaxed">{p.body}</div>
            </div>
          ))}
        </div>
        <label className="flex items-start gap-3 p-5 border-t border-[var(--rs-border)] bg-[var(--rs-surface)] cursor-pointer hover:bg-[var(--rs-surface-2)] transition-colors">
          <input
            type="checkbox"
            checked={!!agbAccepted}
            onChange={(e) => setAgbAccepted(e.target.checked)}
            className="mt-1 w-5 h-5 accent-[var(--rs-primary)] cursor-pointer"
            data-testid="agb-accept-checkbox"
          />
          <span className="text-sm font-bold leading-snug">{t("agb_accept_label")}</span>
        </label>
      </div>
    </div>
  );
};

const RentalFlow = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [stations, setStations] = useState([]);
  const [agb, setAgb] = useState(null);
  const [step, setStep] = useState(0);
  const [vehicle, setVehicle] = useState(null);
  const [stationId, setStationId] = useState(null); // chosen for flexible vehicles

  // Vito state
  const [destination, setDestination] = useState("ch");
  const [vitoStartDate, setVitoStartDate] = useState(todayDate());
  const [vitoStartTime, setVitoStartTime] = useState("09:00");
  const [vitoEndDate, setVitoEndDate] = useState(addDays(todayDate(), 1));
  const [vitoEndTime, setVitoEndTime] = useState("09:00");
  const days = useMemo(() => {
    if (!vitoStartDate || !vitoEndDate) return 1;
    const startMs = new Date(`${vitoStartDate}T${vitoStartTime || "00:00"}`).getTime();
    const endMs = new Date(`${vitoEndDate}T${vitoEndTime || "00:00"}`).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return 1;
    return Math.max(1, Math.min(14, Math.ceil((endMs - startMs) / (24 * 60 * 60 * 1000))));
  }, [vitoStartDate, vitoStartTime, vitoEndDate, vitoEndTime]);
  const setDays = (n) => {
    // legacy preset helper: setting days adjusts the end date
    const safe = Math.max(1, Math.min(14, parseInt(n, 10) || 1));
    setVitoEndDate(addDays(vitoStartDate, safe));
  };
  const [promo, setPromo] = useState(null);

  // Master state
  const [packageHours, setPackageHours] = useState(3);
  const [masterStartDate, setMasterStartDate] = useState(todayDate());
  const [masterStartTime, setMasterStartTime] = useState("09:00");
  const [expectedKm, setExpectedKm] = useState("");

  const [customer, setCustomer] = useState({
    full_name: "", company: "", address: "", city: "", postal_code: "",
    phone: "", email: "", license_number: "", date_of_birth: "",
  });
  const [idPhoto, setIdPhoto] = useState("");
  const [idPhotoBack, setIdPhotoBack] = useState("");
  const [signature, setSignature] = useState("");
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [defectsNote, setDefectsNote] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const tariffType = vehicle?.tariff_type;
  const isVito = tariffType === "vito_daily";
  const isMaster = tariffType === "master_package";

  const rentalStart = isVito
    ? `${vitoStartDate}T${vitoStartTime}`
    : isMaster
    ? `${masterStartDate}T${masterStartTime}`
    : "";
  const rentalEnd = isVito
    ? (vitoEndDate && vitoEndTime ? `${vitoEndDate}T${vitoEndTime}` : "")
    : isMaster
    ? (() => {
        if (!masterStartDate || !masterStartTime) return "";
        const [hh, mm] = masterStartTime.split(":").map((x) => parseInt(x, 10));
        const start = new Date(`${masterStartDate}T${pad(hh)}:${pad(mm)}:00`);
        start.setHours(start.getHours() + packageHours);
        return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}`;
      })()
    : "";

  const priceChf = useMemo(() => {
    if (isVito) {
      // Apply promo override on the frontend so the customer sees the discounted price
      if (promo === "vito_3d" && destination === "ch" && days === 3) return 500;
      if (promo === "vito_7d_eu" && destination === "eu" && days === 7) return 1400;
      return computeVitoPrice(destination, vitoStartDate, days);
    }
    if (isMaster) {
      if (promo === "master_6h" && packageHours === 6) return 100;
      const pkg = MASTER_PACKAGES.find((p) => p.hours === packageHours);
      return pkg ? pkg.price_chf : 0;
    }
    return 0;
  }, [isVito, isMaster, destination, vitoStartDate, days, packageHours, promo]);

  const summary = isVito
    ? `${days} ${days === 1 ? "Tag" : "Tage"} · ${destination === "eu" ? "Ausland (EU)" : "Schweiz (CH)"} · ${isPureWeekend(vitoStartDate, days) ? "Wochenend-Tarif" : "Werktage-Tarif"}`
    : isMaster
    ? `Paket ${packageHours}h · ${MASTER_PACKAGES.find((p) => p.hours === packageHours)?.km_included || 0} km inkl.`
    : "";

  useEffect(() => {
    api
      .get("/vehicles")
      .then(({ data }) => {
        const vs = data.vehicles || [];
        setVehicles(vs);
        setStations(data.stations || []);
        // Deep-link params: ?station=<id> (from /standort/...) and ?promo=vito_3d (from PromoBanner)
        const params = new URLSearchParams(window.location.search);
        const presetStation = params.get("station");
        const promo = params.get("promo");
        const presetDays = parseInt(params.get("days") || "0", 10);
        if (promo === "vito_3d") {
          // Auto-select a Vito vehicle and 3-day CH plan
          const vito = vs.find((v) => v.tariff_type === "vito_daily");
          if (vito) {
            setVehicle({ ...vito, _preferred_station_id: presetStation || vito.station_id });
          }
          setDays(3);
          setDestination("ch");
          setPromo("vito_3d");
          toast.success("Aktionspreis CHF 500.– aktiv: 3 Tage Vito");
        } else if (promo === "master_6h") {
          const master = vs.find((v) => v.tariff_type === "master_package");
          if (master) {
            setVehicle({ ...master, _preferred_station_id: presetStation || master.station_id });
          }
          setPackageHours(6);
          setPromo("master_6h");
          toast.success("Aktionspreis CHF 100.– aktiv: Master 6h");
        } else if (promo === "vito_7d_eu") {
          const vito = vs.find((v) => v.tariff_type === "vito_daily");
          if (vito) {
            setVehicle({ ...vito, _preferred_station_id: presetStation || vito.station_id });
          }
          setDays(7);
          setDestination("eu");
          setPromo("vito_7d_eu");
          toast.success("Aktionspreis CHF 1'400.– aktiv: 7 Tage Vito Europa");
        } else if (presetStation && vs.length) {
          const match = vs.find(
            (v) =>
              v.station_id === presetStation ||
              (v.flexible_stations || []).includes(presetStation)
          );
          if (match) {
            setVehicle({ ...match, _preferred_station_id: presetStation });
          }
        }
        if (presetDays >= 1 && presetDays <= 14) setDays(presetDays);
      })
      .catch(() => toast.error(t("vehicles_load_error")));
    api
      .get("/agb")
      .then(({ data }) => setAgb(data))
      .catch(() => {});
  }, []);

  // Reset station when vehicle changes.
  // Priority: customer's picked station (from new VehicleStep flow) > default > first flexible.
  useEffect(() => {
    if (!vehicle) return;
    if (vehicle._preferred_station_id) {
      setStationId(vehicle._preferred_station_id);
    } else if (vehicle.station_id) {
      setStationId(vehicle.station_id);
    } else if (vehicle.flexible_stations && vehicle.flexible_stations.length > 0) {
      setStationId(vehicle.flexible_stations[0]);
    } else setStationId(null);
  }, [vehicle]);

  useEffect(() => {
    if (user && user.role === "customer") {
      api.get("/me/profile").then(({ data }) => {
        setCustomer((prev) => ({
          full_name: data.name || prev.full_name,
          address: data.address || prev.address,
          city: data.city || prev.city,
          postal_code: data.postal_code || prev.postal_code,
          phone: data.phone || prev.phone,
          email: data.email || prev.email,
          license_number: data.license_number || prev.license_number,
          date_of_birth: data.date_of_birth || prev.date_of_birth,
        }));
      }).catch(() => {});
    }
  }, [user]);

  const labels = [t("step_vehicle"), t("step_config"), t("step_customer"), t("step_id"), t("step_signature"), t("step_review")];

  const canProceed = () => {
    if (step === 0) return !!vehicle;
    if (step === 1) {
      // Station must be valid: default station OR one of the flexible alternates
      const allowed = [
        ...(vehicle?.station_id ? [vehicle.station_id] : []),
        ...((vehicle?.flexible_stations) || []),
      ];
      const okStation = allowed.length === 0 ? true : allowed.includes(stationId);
      if (!okStation) return false;
      if (isVito) {
        const d = parseInt(days, 10);
        return !!destination && !!vitoStartDate && !!vitoStartTime && d >= 1 && d <= 14 && priceChf > 0;
      }
      if (isMaster) {
        if (!masterStartDate || !isMasterDateOk(masterStartDate)) return false;
        const h = parseInt((masterStartTime || "08:00").split(":")[0], 10);
        if (h < MASTER_OPEN || h >= MASTER_CLOSE) return false;
        return [3, 6, 10].includes(packageHours);
      }
      return false;
    }
    if (step === 2) {
      const c = customer;
      return !!(c.full_name && c.address && c.city && c.postal_code && c.phone && c.license_number);
    }
    if (step === 3) return !!idPhoto;
    if (step === 4) return !!signature;
    if (step === 5) return !!agbAccepted;
    return false;
  };

  const next = () => {
    if (!canProceed()) {
      if (step === 5 && !agbAccepted) toast.error(t("agb_required"));
      else if (step === 1 && isMaster && !isMasterDateOk(masterStartDate)) toast.error(t("wednesday_blocked"));
      else toast.error(t("required"));
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  // Smoothly scroll back to top whenever the user moves to a new step.
  // Without this, on mobile the user lands mid-page right above the WEITER
  // button instead of seeing the new step title.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const submitRequest = async () => {
    if (!vehicle) {
      toast.error(t("required"));
      return;
    }
    setRequesting(true);
    try {
      const isoStart = isVito ? vitoStartDate : masterStartDate;
      const isoEnd = isVito && days ? addDays(vitoStartDate, days) : null;
      const payload = {
        vehicle_id: vehicle.id,
        rental_type: isVito ? "vito_daily" : "master_package",
        rental_start: isoStart,
        rental_end: isoEnd,
        destination: isVito ? destination : null,
        days: isVito ? days : null,
        package_hours: isMaster ? packageHours : null,
        expected_km: isMaster && expectedKm ? parseInt(expectedKm, 10) : null,
        station_id: stationId,
        promo: promo || null,
      };
      const { data } = await api.post("/booking-requests", payload);
      toast.success("Anfrage gesendet — wir melden uns per E-Mail");
      navigate(`/my?req=${data.id}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Bitte einloggen um eine Anfrage zu stellen");
        navigate("/login?next=/");
      } else {
        toast.error(e?.response?.data?.detail || "Anfrage fehlgeschlagen");
      }
    } finally {
      setRequesting(false);
    }
  };

  const submit = async () => {
    if (!agbAccepted) {
      toast.error(t("agb_required"));
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        vehicle_id: vehicle.id,
        customer,
        rental_type: tariffType,
        rental_start: rentalStart,
        rental_end: rentalEnd,
        id_photo_base64: idPhoto,
        id_photo_back_base64: idPhotoBack || null,
        signature_base64: signature,
        agb_accepted: true,
        agb_version: agb?.version,
        defects_note: defectsNote || null,
        payment_method: paymentMethod,
        station_id: stationId,
      };
      if (isVito) {
        body.destination = destination;
        body.days = days;
        if (promo === "vito_3d" && destination === "ch" && days === 3) {
          body.promo = "vito_3d";
        } else if (promo === "vito_7d_eu" && destination === "eu" && days === 7) {
          body.promo = "vito_7d_eu";
        }
      } else if (isMaster) {
        body.package_hours = packageHours;
        body.expected_km = expectedKm ? parseInt(expectedKm, 10) : null;
        if (promo === "master_6h" && packageHours === 6) {
          body.promo = "master_6h";
        }
      }
      const { data } = await api.post("/contracts", body);
      navigate(`/confirmation/${data.id}?n=${encodeURIComponent(data.contract_number)}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(t("conflict_error"));
      } else {
        toast.error(e?.response?.data?.detail || t("submit_error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <Toaster position="top-center" />
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[calc(100vh-5rem)]">
        {/* Sidebar: hidden until a vehicle is selected. On mobile it sits BELOW the form (order-2). */}
        {vehicle && (
          <div className="order-2 md:order-1 md:col-span-4 lg:col-span-3">
            <RentalSummary
              vehicle={vehicle}
              customer={customer}
              rentalType={tariffType || "vito_daily"}
              rentalStart={rentalStart}
              rentalEnd={rentalEnd}
              durationHours={isVito ? days * 24 : packageHours}
              days={isVito ? days : undefined}
              expectedKm={parseInt(expectedKm || "0", 10)}
              priceChf={priceChf}
              station={stations.find((s) => s.id === stationId)}
            />
          </div>
        )}
        <div
          className={`order-1 md:order-2 ${
            vehicle ? "md:col-span-8 lg:col-span-9" : "md:col-span-12"
          } p-6 md:p-10 lg:p-14 pb-32 lg:pb-14 xl:pr-[300px]`}
        >
          <StepIndicator step={step} labels={labels} />
          <div className="mt-10">
            {step === 0 && <VehicleStep vehicles={vehicles} stations={stations} selected={vehicle} onSelect={setVehicle} />}
            {step === 1 && isVito && (
              <VitoConfigStep
                destination={destination}
                setDestination={setDestination}
                startDate={vitoStartDate}
                setStartDate={setVitoStartDate}
                startTime={vitoStartTime}
                setStartTime={setVitoStartTime}
                endDate={vitoEndDate}
                setEndDate={setVitoEndDate}
                endTime={vitoEndTime}
                setEndTime={setVitoEndTime}
                days={days}
                vehicle={vehicle}
                stations={stations}
                stationId={stationId}
                setStationId={setStationId}
                isStaff={user?.role === "admin" || user?.role === "station_manager"}
              />
            )}
            {step === 1 && isMaster && (
              <MasterConfigStep
                packageHours={packageHours}
                setPackageHours={setPackageHours}
                startDate={masterStartDate}
                setStartDate={setMasterStartDate}
                startTime={masterStartTime}
                setStartTime={setMasterStartTime}
                expectedKm={expectedKm}
                setExpectedKm={setExpectedKm}
                vehicle={vehicle}
                stations={stations}
                stationId={stationId}
                setStationId={setStationId}
                isStaff={user?.role === "admin" || user?.role === "station_manager"}
              />
            )}
            {step === 2 && (
              <CustomerStep
                customer={customer}
                setCustomer={setCustomer}
                isStaff={user?.role === "admin" || user?.role === "station_manager"}
              />
            )}
            {step === 3 && <IdStep idPhoto={idPhoto} setIdPhoto={setIdPhoto} idPhotoBack={idPhotoBack} setIdPhotoBack={setIdPhotoBack} />}
            {step === 4 && <SignatureStep signature={signature} setSignature={setSignature} />}
            {step === 5 && (
              <ReviewStep
                vehicle={vehicle}
                customer={customer}
                rentalType={tariffType}
                rentalStart={rentalStart}
                rentalEnd={rentalEnd}
                priceChf={priceChf}
                summary={summary}
                idPhoto={idPhoto}
                signature={signature}
                agb={agb}
                agbAccepted={agbAccepted}
                setAgbAccepted={setAgbAccepted}
                defectsNote={defectsNote}
                setDefectsNote={setDefectsNote}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
              />
            )}
          </div>

          <div className="mt-12 mb-24 sm:mb-0 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t border-[var(--rs-border)] pt-6">
            <button type="button" className="rs-btn-secondary disabled:opacity-30" disabled={step === 0} onClick={back} data-testid="step-back-btn">
              <ArrowLeft size={16} className="mr-2" />
              {t("btn_back")}
            </button>

            {/* Vito-promo Anfrage shortcut: only for logged-in customers on the configuration step */}
            {step === 1 && isVito && promo && user?.role === "customer" && (
              <button
                type="button"
                className="rs-btn-secondary !border-[#D4AF37] !text-[#D4AF37] hover:!bg-[#D4AF37] hover:!text-black"
                onClick={submitRequest}
                disabled={requesting}
                data-testid="step-request-btn"
              >
                {requesting ? t("saving") : "Anfrage stellen"}
                <Mail size={16} className="ml-2" />
              </button>
            )}

            {step < STEPS.length - 1 ? (
              <button type="button" className="rs-btn-primary" onClick={next} data-testid="step-next-btn">
                {t("btn_next")}
                <ArrowRight size={16} className="ml-2" />
              </button>
            ) : (
              <button type="button" className="rs-btn-primary" onClick={submit} disabled={submitting || !agbAccepted} data-testid="contract-submit-btn">
                {submitting ? t("saving") : t("btn_confirm")}
                <FileCheck size={18} className="ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Marketing blocks — only on first step (vehicle pick) so we don't distract during the wizard */}
      {step === 0 && (
        <>
          <TrustSection />
          <ValuesSection />
        </>
      )}
      <Footer />
    </div>
  );
};

export default RentalFlow;
